/**
 * `AgentSurface` — the Assistant hub's tab strip (NAVEGADOR · TELEGRAM ·
 * WHATSAPP · E-MAIL, in that D-10 order) plus the selected section's panel,
 * under the identity hero.
 *
 * There is no SETTINGS tab: the assistant-hub redesign (S4) eliminated it and
 * moved the cross-cutting residue into the hero's gear `ConfigDialog`.
 * Everything settings-near-function now lives on the thing it configures —
 * channels on their card chips, identity on the hero, schedules in their own
 * rail pane.
 *
 * ONE SHAPE, as of the chat-left round: the FOCUS PANE beside the conversation
 * (35–65 %). The `variant="page"` alternative — big icon-on-top cards on a
 * `max-w-3xl` column — died with the full-page `/agent` route, which is now a
 * deep-link entry that opens this pane instead (`layouts/AgentLayout.tsx`). Its
 * tile grid spent ~170 px of vertical space that a 35 %-wide column cannot
 * afford, so the compact tab row is the only shape left and every `isPane`
 * branch is gone.
 *
 * Section state is OWNED BY THE HOST (controlled `section`/`onSelect`): a focus
 * pane has no URL of its own, so the host keeps it in local state and seeds it
 * from the one-shot `agentPaneInitial` handoff when a deep link asked for a
 * particular tab.
 */
import { Globe, Mail, Maximize2, MessageCircle, Minimize2, Send, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { ChannelPane } from "@/components/agent/ChannelPane";
import { EmailPane } from "@/components/agent/EmailPane";
import { AgentBrowserLayout } from "@/layouts/AgentBrowserLayout";
import { AssistantHero } from "@/components/agent/AssistantHero";
import { AssistantAvatar } from "@/components/agent/AssistantAvatar";
import { assistantDisplayName, useAssistantIdentity } from "@/hooks/useAssistantIdentity";

export type AgentSection = "browser" | "email" | "telegram" | "whatsapp" | "settings";

export function isAgentSection(v: string | null): v is AgentSection {
  return (
    v === "browser" ||
    v === "email" ||
    v === "telegram" ||
    v === "whatsapp" ||
    v === "settings"
  );
}

/**
 * Each section carries its own accent: the channels get real, recognisable
 * colour, so a lawyer scanning for WhatsApp finds it by hue before reading a
 * word.
 */
interface SectionDef {
  id: AgentSection;
  icon: LucideIcon;
  labelKey: string;
  /** Icon colour when the card is idle. */
  idle: string;
  /** Card treatment when selected — tint + border + icon. */
  active: string;
  activeIcon: string;
}

// Rail order = D-10 (operator 2026-07-27): Navegador · Telegram · WhatsApp ·
// E-mail — Telegram-first steering by PROMINENCE AND POSITION (§2.9): the
// starting channel sits right after the browser; WhatsApp (needs a dedicated
// chip) next; e-mail last of the channels because it needs no setup at all.
export const AGENT_SECTIONS: SectionDef[] = [
  {
    // The agent's live browser — its own capability, first among equals. Violet
    // keeps it distinct from the channel hues (indigo/sky/emerald) while staying
    // in the same jewel-tone family; the Globe + label remove any ambiguity.
    id: "browser",
    icon: Globe,
    labelKey: "agent.sections.browser",
    idle: "text-violet-500/90 dark:text-violet-300/85",
    active:
      "border-violet-400/70 bg-violet-50 dark:border-violet-400/50 dark:bg-violet-950/40",
    activeIcon: "text-violet-500 dark:text-violet-300",
  },
  {
    id: "telegram",
    icon: Send,
    labelKey: "agent.sections.telegram",
    idle: "text-sky-500/90 dark:text-sky-300/85",
    active: "border-sky-400/70 bg-sky-50 dark:border-sky-400/50 dark:bg-sky-950/40",
    activeIcon: "text-sky-500 dark:text-sky-300",
  },
  {
    id: "whatsapp",
    icon: MessageCircle,
    labelKey: "agent.sections.whatsapp",
    idle: "text-emerald-500/90 dark:text-emerald-300/85",
    active:
      "border-emerald-400/70 bg-emerald-50 dark:border-emerald-400/50 dark:bg-emerald-950/40",
    activeIcon: "text-emerald-500 dark:text-emerald-300",
  },
  {
    id: "email",
    icon: Mail,
    labelKey: "agent.sections.email",
    idle: "text-indigo-500/90 dark:text-indigo-300/85",
    active:
      "border-indigo-400/70 bg-indigo-50 dark:border-indigo-400/50 dark:bg-indigo-950/40",
    activeIcon: "text-indigo-500 dark:text-indigo-300",
  },
];

export function AgentSurface({
  section,
  onSelect,
  maximized,
  onToggleMaximize,
  onClose,
  initialConfigOpen,
}: {
  section: AgentSection;
  onSelect: (id: AgentSection) => void;
  /** In-pane maximize (§3.2): active tab fills the pane, the hero card
   *  collapses to a slim strip. Pane-host local state (resets on pane close). */
  maximized: boolean;
  onToggleMaximize: () => void;
  /** The pane close, relocated from the host chrome into the hero cluster. */
  onClose: () => void;
  /** §4.5 alias: a `?section=settings` deep link opens the gear dialog (the
   *  tab it used to name was retired in S4). */
  initialConfigOpen?: boolean;
}) {
  const { t } = useTranslation();
  const { assistant, owner, refresh: refreshIdentity } = useAssistantIdentity();

  /** Navegador auto-maximize (operator 2026-07-27): the browser's bottom
   *  trust line ("Faça login nos sites… Credenciais ficam salvas…") can sit
   *  below the fold while the full header is showing — so the moment the
   *  lawyer actually TYPES into the live browser, give the tab the whole pane
   *  (which brings that line into view). Capture-phase so we see keys the
   *  noVNC canvas consumes; never preventDefault — the keystroke still lands
   *  in the remote browser. Plain chars/Enter/Backspace only: modifiers,
   *  Esc and Tab are chrome, not typing. */
  const autoMaximizeOnType = (e: React.KeyboardEvent) => {
    if (maximized || section !== "browser") return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.length === 1 || e.key === "Enter" || e.key === "Backspace") {
      onToggleMaximize();
    }
  };

  const tabs = (
    // The maximize control rides the TAB ROW, not the hero (operator
    // 2026-07-27): it acts on the tabbed area below, so it sits on its
    // container — absolute so the centered strip stays centered.
    <div className="relative flex shrink-0 items-center px-9">
      <div
        role="tablist"
        aria-label={t("agent.sectionsLabel")}
        className="flex flex-1 flex-wrap items-center justify-center gap-1.5"
      >
      {AGENT_SECTIONS.map((sec) => {
        const Icon = sec.icon;
        const selected = section === sec.id;
        return (
          <button
            key={sec.id}
            type="button"
            role="tab"
            aria-selected={selected}
            data-testid={`agent-section-${sec.id}`}
            onClick={() => onSelect(sec.id)}
            className={cn(
              "group flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 transition-all",
              selected
                ? cn(sec.active, "shadow-sm ring-1 ring-inset ring-white/5")
                : "border-cream-200/70 bg-cream-50/40 hover:border-cream-300 hover:bg-cream-100/60 dark:border-coffee-800 dark:bg-coffee-950/40 dark:hover:border-coffee-700 dark:hover:bg-coffee-900/60",
            )}
          >
            <Icon
              size={16}
              strokeWidth={selected ? 2 : 1.75}
              aria-hidden
              className={cn("transition-colors", selected ? sec.activeIcon : sec.idle)}
            />
            <span
              className={cn(
                "text-body-sm transition-colors",
                selected
                  ? "font-semibold text-coffee-900 dark:text-cream-100"
                  : "text-coffee-600 dark:text-cream-400",
              )}
            >
              {t(sec.labelKey)}
            </span>
          </button>
        );
      })}
      </div>
      {/* Prominent by design (operator 2026-07-27): gold accent + border +
          larger glyph — expanding the monitoring area is a primary action on
          this row, not quiet chrome. */}
      <button
        type="button"
        data-testid="pane-maximize"
        onClick={onToggleMaximize}
        aria-label={t("agent.hero.maximize")}
        title={t("agent.hero.maximize")}
        className="absolute right-0 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-lg border border-gold-500/60 bg-gold-400/10 p-2 text-gold-700 shadow-sm transition-colors hover:border-gold-500 hover:bg-gold-400/25 dark:border-gold-500/50 dark:bg-gold-500/10 dark:text-gold-400 dark:hover:bg-gold-500/25 dark:hover:text-gold-300"
      >
        <Maximize2 size={18} strokeWidth={2.25} />
      </button>
    </div>
  );

  const panel = (
    <div
      data-testid="agent-section-panel"
      onKeyDownCapture={autoMaximizeOnType}
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-cream-200/70 dark:border-coffee-800",
        // The browser fills the card edge-to-edge (it brings its own chrome +
        // background); the other sections keep the inset panel treatment.
        section === "browser" ? "" : "bg-white/70 p-2 dark:bg-coffee-900/60",
      )}
    >
      {/* The agent's live browser, embedded (no redundant Back/title chrome). */}
      {section === "browser" && <AgentBrowserLayout embedded />}
      {/* Email rides the `mail.*` read surface (C3). Its own gate resolves
          method-not-found to the honest placeholder on a runtime that predates
          it. NEVER gate it on channels.list (email's status there is hard-coded
          `disconnected`). */}
      {section === "email" && <EmailPane icon={Mail} fill />}
      {section === "telegram" && (
        <ChannelPane channel="telegram" icon={Send} fill />
      )}
      {section === "whatsapp" && (
        <ChannelPane
          channel="whatsapp"
          icon={MessageCircle}
          fill
          // The not-connected CTA's Telegram-first nudge lands the lawyer on
          // the Telegram tab (§2.9 steering — never gating).
          onGoToTelegram={() => onSelect("telegram")}
        />
      )}
    </div>
  );

  // The header collapses between two shapes — the full hero card + tabs when
  // restored, ONE slim strip when maximized (§3.2) — WITHOUT disturbing the
  // panel. Because the header is a single element and `{panel}` is always the
  // last child of the same parent, React preserves the live tab (browser WS,
  // mailbox, thread scroll) across the toggle instead of remounting it — which
  // is what makes maximize a true in-pane re-allocation, not a reload. The pane
  // geometry, width and drag separator never move; Esc-to-restore lives in the
  // pane-host wrapper.
  const name = assistantDisplayName(assistant);
  const stripBtn =
    "rounded-md p-1 text-coffee-500 transition-colors hover:bg-cream-100 hover:text-coffee-800 dark:text-cream-500 dark:hover:bg-coffee-800 dark:hover:text-cream-100";

  const header = maximized ? (
    <div
      data-testid="assistant-hero-strip"
      className="flex shrink-0 items-center gap-2 rounded-lg border border-cream-200/70 bg-white/60 px-2 py-1 dark:border-coffee-800 dark:bg-coffee-900/50"
    >
      <div role="tablist" aria-label={t("agent.sectionsLabel")} className="flex items-center gap-1">
        {AGENT_SECTIONS.map((sec) => {
          const Icon = sec.icon;
          const selected = section === sec.id;
          return (
            <button
              key={sec.id}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-label={t(sec.labelKey)}
              title={t(sec.labelKey)}
              data-testid={`agent-section-${sec.id}`}
              onClick={() => onSelect(sec.id)}
              className={cn(
                "flex items-center justify-center rounded-md border p-1 transition-all",
                selected
                  ? cn(sec.active, "shadow-sm")
                  : "border-transparent hover:border-cream-300 hover:bg-cream-100/60 dark:hover:border-coffee-700 dark:hover:bg-coffee-900/60",
              )}
            >
              <Icon
                size={15}
                strokeWidth={selected ? 2 : 1.75}
                aria-hidden
                className={cn(selected ? sec.activeIcon : sec.idle)}
              />
            </button>
          );
        })}
      </div>
      <div className="ml-1 flex min-w-0 items-center gap-1.5">
        <AssistantAvatar identity={assistant} size={20} testid="assistant-avatar" />
        <span className="truncate text-body-sm font-semibold text-coffee-800 dark:text-cream-200">
          {name}
        </span>
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-0.5">
        <button
          type="button"
          data-testid="pane-restore"
          onClick={onToggleMaximize}
          aria-label={t("agent.hero.restore")}
          title={t("agent.hero.restore")}
          className="flex items-center justify-center rounded-lg border border-gold-500/60 bg-gold-400/10 p-1.5 text-gold-700 transition-colors hover:border-gold-500 hover:bg-gold-400/25 dark:border-gold-500/50 dark:bg-gold-500/10 dark:text-gold-400 dark:hover:bg-gold-500/25 dark:hover:text-gold-300"
        >
          <Minimize2 size={16} strokeWidth={2.25} />
        </button>
        <button
          type="button"
          data-testid="focus-pane-close"
          onClick={onClose}
          aria-label={t("common.close")}
          title={t("common.close")}
          className={stripBtn}
        >
          <X size={15} />
        </button>
      </div>
    </div>
  ) : (
    // The hero business card replaced the per-tab summary sub-header entirely
    // (operator 2026-07-27): the contacts + status now live on the card, so no
    // tab renders an orientation card anymore.
    <div className="flex shrink-0 flex-col gap-2">
      <AssistantHero
        identity={assistant}
        owner={owner}
        onSelectSection={onSelect}
        onIdentityChanged={refreshIdentity}
        onClose={onClose}
        initialConfigOpen={initialConfigOpen}
      />
      {tabs}
    </div>
  );

  return (
    <div
      data-testid={maximized ? "agent-surface-maximized" : undefined}
      className={cn("flex min-h-0 flex-1 flex-col p-2", maximized ? "gap-1.5" : "gap-2")}
    >
      {header}
      {panel}
    </div>
  );
}
